import { useState } from 'react';
import {
  Plus,
  Trash2,
  Type,
  Mail,
  Hash,
  Phone,
  Calendar,
  Upload,
  List,
  CheckSquare,
  Star,
  ShieldCheck,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import {
  Field,
  Step,
  createDefaultRatingOptions,
  RatingOption,
  PERSON_FIELD_PRESETS,
  isReservedFieldKey,
} from '@dynamic-flow/types';
import MDEditor from '@uiw/react-md-editor';
import { normalizeToVar } from '@/utils/normalization';
import {
  isEventRegistrationFlow,
  warnIfSlugCollidesWithReserved,
} from '@/utils/personFieldKeys';
import { ConsentConfig } from './ConsentConfig';
import { useFlowBuilder } from '@/hooks/useFlowBuilder';
import MarkdownEditor from './MarkdownEditor';

/** Generic email/phone types — hide on event forms so staff use reserved Person presets. */
const EVENT_HIDDEN_FIELD_TYPE_IDS = new Set(['email_input', 'phone_input']);

interface FieldBuilderProps {
  step: Step | null;
  selectedFieldId: string | null;
  onStepChange: (step: Step) => void;
  onSelectField: (fieldId: string | null) => void;
  allSteps?: Step[]; // เพิ่มเพื่อให้เข้าถึง previous steps
}
const FIELD_TYPES = [
  {
    id: 'text_input',
    label: 'Text Input',
    icon: Type,
    description: 'Single line text',
    hasPlaceholder: true,
  },
  {
    id: 'textarea',
    label: 'Text Area',
    icon: Type,
    description: 'Multi-line text',
    hasPlaceholder: true,
  },
  {
    id: 'email_input',
    label: 'Email',
    icon: Mail,
    description: 'Email address',
    hasPlaceholder: true,
  },
  {
    id: 'number_input',
    label: 'Number',
    icon: Hash,
    description: 'Numeric input',
    hasPlaceholder: true,
  },
  {
    id: 'phone_input',
    label: 'Phone Number',
    icon: Phone,
    description: 'Phone with country code',
    hasPlaceholder: true,
  },
  {
    id: 'date_picker',
    label: 'Date',
    icon: Calendar,
    description: 'Date selection',
    hasPlaceholder: false,
  },
  {
    id: 'single_choice',
    label: 'Single Choice',
    icon: List,
    description: 'Radio buttons',
    hasPlaceholder: false,
  },
  {
    id: 'multiple_choice',
    label: 'Multiple Choice',
    icon: CheckSquare,
    description: 'Checkboxes',
    hasPlaceholder: false,
  },
  {
    id: 'file_upload',
    label: 'File Upload',
    icon: Upload,
    description: 'File selection',
    hasPlaceholder: false,
  },
  {
    id: 'rating',
    label: 'Rating',
    icon: Star,
    description: 'Rating',
    hasPlaceholder: false,
  },
  {
    id: 'consent',
    label: 'Consent',
    icon: ShieldCheck, // or FileCheck from lucide-react
    description: 'Consent management',
    hasPlaceholder: false,
  },
] as const;

const FILE_TYPE_OPTIONS = [
  { id: 'pdf-type', label: 'PDF', value: '.pdf' },
  { id: 'jpg-type', label: 'JPEG', value: '.jpg' },
  { id: 'png-type', label: 'PNG', value: '.png' },
] as const;

// Helper function to get validation value from different validation formats
const getValidationValue = (
  validation: any,
  key: 'min' | 'max' | 'max_length'
): number | undefined => {
  if (!validation) return undefined;

  // Case 1: Direct property access (e.g., validation.min)
  if (validation[key] !== undefined) {
    return validation[key];
  }

  // Case 2: Array format with type (e.g., [{type: 'min', value: 5}])
  if (Array.isArray(validation)) {
    const rule = validation.find((rule: any) => rule.type === key);
    return rule?.value;
  }

  // Case 3: Object with type property (e.g., {type: 'min', value: 5})
  if (validation.type === key) {
    return validation.value;
  }

  return undefined;
};

const slugifyRatingValue = (label: string) =>
  label
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

const renumberRatingOptions = (options: RatingOption[]): RatingOption[] =>
  options.map((option, index) => ({
    ...option,
    score: index + 1,
  }));

const SCORE_BADGE_COLORS = [
  'bg-amber-100 text-amber-800',
  'bg-orange-100 text-orange-800',
  'bg-yellow-100 text-yellow-800',
  'bg-amber-200 text-amber-900',
  'bg-orange-200 text-orange-900',
];

export function FieldBuilder({
  step,
  selectedFieldId,
  onStepChange,
  onSelectField,
  allSteps = [],
}: FieldBuilderProps) {
  const [showAddField, setShowAddField] = useState(false);
  const { mode } = useFlowBuilder();
  // const [selectedFieldType, setSelectedFieldType] = useState<string>('');

  const stepsForEventCheck = allSteps.length > 0 ? allSteps : [step].filter(Boolean) as Step[];
  const isEventFlow = isEventRegistrationFlow(stepsForEventCheck);
  const choosableFieldTypes = isEventFlow
    ? FIELD_TYPES.filter((ft) => !EVENT_HIDDEN_FIELD_TYPE_IDS.has(ft.id))
    : FIELD_TYPES;

  if (!step) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-2">👈</div>
          <p className="text-sm">Select a step to manage fields</p>
        </div>
      </div>
    );
  }

  // Handle landing_page and end_page types
  if (
    (step as any).type === 'landing_page' ||
    (step as any).type === 'end_page'
  ) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">
                {(step as any).type === 'landing_page'
                  ? 'Landing Page'
                  : 'End Page'}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                {(step as any).type === 'landing_page'
                  ? 'This page will be shown at the start of the form'
                  : 'This page will be shown at the end of the form'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Page Title *
            </label>
            <input
              type="text"
              value={step.title}
              onChange={(e) => onStepChange({ ...step, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="Enter page title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <MarkdownEditor
              value={step.description || ''}
              onChange={(value) =>
                onStepChange({ ...step, description: value || '' })
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image URL (coverImageUrl)
            </label>
            <input
              type="url"
              value={(step as any).coverImageUrl || ''}
              onChange={(e) =>
                onStepChange({ ...step, coverImageUrl: e.target.value } as any)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="https://example.com/image.jpg"
            />
            {(step as any).coverImageUrl && (
              <div className="mt-2">
                <img
                  src={(step as any).coverImageUrl}
                  alt="Preview"
                  className="max-w-full h-auto rounded border"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Markdown Text (markdownContent)
            </label>
            <MarkdownEditor
              value={(step as any).markdownContent || ''}
              onChange={(value) =>
                onStepChange({ ...step, markdownContent: value || '' } as any)
              }
            />
            <p className="text-xs text-gray-500 mt-1">
              Edit using Markdown. It will be rendered as formatted text.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleAddField = (fieldType: string, reservedKey?: string) => {
    const fieldTypeInfo = FIELD_TYPES.find((t) => t.id === fieldType);
    if (!fieldTypeInfo) return;

    const preset = PERSON_FIELD_PRESETS.find((p) => p.key === reservedKey);
    const label = preset?.label ?? `New ${fieldTypeInfo.label}`;
    const fieldKey = reservedKey ?? normalizeToVar(label);

    const newField: Field = {
      id: `field_${Date.now()}`,
      type: fieldType as any,
      label,
      fieldKey,
      required: false,
      placeholder: '',
      options: ['single_choice', 'multiple_choice'].includes(fieldType)
        ? [
            { id: 'option1', label: 'Option 1', value: 'option1' },
            { id: 'option2', label: 'Option 2', value: 'option2' },
          ]
        : fieldType === 'rating'
          ? createDefaultRatingOptions()
          : undefined,
      binding:
        reservedKey === 'event_id'
          ? { type: 'query_param', name: 'eventId', required: false }
          : undefined,
    };

    const updatedStep = {
      ...step,
      fields: [...step.fields, newField],
    };

    onStepChange(updatedStep);
    onSelectField(newField.id);
    setShowAddField(false);
  };

  const handleDeleteField = (fieldId: string) => {
    const updatedStep = {
      ...step,
      fields: step.fields.filter((field) => field.id !== fieldId),
    };

    onStepChange(updatedStep);

    if (selectedFieldId === fieldId) {
      onSelectField(
        updatedStep.fields.length > 0 ? updatedStep.fields[0].id : null
      );
    }
  };

  const handleUpdateField = (fieldId: string, updates: Partial<Field>) => {
    // Validate binding name if binding is enabled
    if (updates.binding && updates.binding.type === 'query_param') {
      if (!updates.binding.name || updates.binding.name.trim() === '') {
        alert('Binding name is required when binding is enabled');
        return;
      }
    }

    const updatedStep = {
      ...step,
      fields: step.fields.map((field) =>
        field.id === fieldId ? { ...field, ...updates } : field
      ),
    };

    onStepChange(updatedStep);
  };

  const handleMoveField = (fieldId: string, direction: 'up' | 'down') => {
    const currentIndex = step.fields.findIndex((field) => field.id === fieldId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= step.fields.length) return;

    const newFields = [...step.fields];
    [newFields[currentIndex], newFields[newIndex]] = [
      newFields[newIndex],
      newFields[currentIndex],
    ];

    onStepChange({ ...step, fields: newFields });
  };

  const selectedField = step.fields.find(
    (field) => field.id === selectedFieldId
  );

  const executeIfEditable = (fn: Function) => {
    if (mode !== 'edit') {
      return;
    }
    fn();
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900">Fields</h3>
            <p className="text-xs text-gray-500 mt-1">Step: {step.title}</p>
          </div>
          {mode === 'edit' && (
            <button
              onClick={() => setShowAddField(true)}
              className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Field
            </button>
          )}
        </div>

        {/* Loop Configuration */}
        <div className="mt-3 p-3 bg-blue-50 border rounded-lg">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-900">
                🔄 Dynamic Loop
              </h4>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={step.loop?.enabled || false}
                  disabled={mode !== 'edit'}
                  onChange={(e) => {
                    const newLoop = step.loop || { enabled: false };
                    executeIfEditable(() =>
                      onStepChange({
                        ...step,
                        loop: { ...newLoop, enabled: e.target.checked },
                      })
                    );
                  }}
                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Enable Loop</span>
              </label>
            </div>

            {step.loop?.enabled && (
              <div className="space-y-3 pl-4 border-l-2 border-blue-200">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Count Source Field
                  </label>
                  <select
                    value={step.loop.sourceFieldId || ''}
                    onChange={(e) => {
                      onStepChange({
                        ...step,
                        loop: { ...step.loop!, sourceFieldId: e.target.value },
                      });
                    }}
                    className="w-full text-sm border border-gray-300 rounded-md px-2 py-1"
                  >
                    <option value="">Select a number field...</option>
                    {/* Get number fields from current step and all previous steps */}
                    {allSteps
                      .flatMap((s) => s.fields)
                      .filter((f) => f.type === 'number_input')
                      .map((field) => {
                        const stepName =
                          allSteps.find((s) => s.fields.includes(field))
                            ?.title || 'Unknown Step';
                        return (
                          <option key={field.id} value={field.id}>
                            {stepName}: {field.label}
                          </option>
                        );
                      })}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose a number field that determines loop count
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Min Count
                    </label>
                    <input
                      type="number"
                      value={step.loop.minCount || 0}
                      disabled={mode !== 'edit'}
                      onChange={(e) => {
                        executeIfEditable(() =>
                          onStepChange({
                            ...step,
                            loop: {
                              ...step.loop!,
                              minCount: parseInt(e.target.value) || 0,
                            },
                          })
                        );
                      }}
                      className="w-full text-sm border border-gray-300 rounded-md px-2 py-1"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Max Count
                    </label>
                    <input
                      type="number"
                      value={step.loop.maxCount || 10}
                      disabled={mode !== 'edit'}
                      onChange={(e) => {
                        executeIfEditable(() =>
                          onStepChange({
                            ...step,
                            loop: {
                              ...step.loop!,
                              maxCount: parseInt(e.target.value) || 10,
                            },
                          })
                        );
                      }}
                      className="w-full text-sm border border-gray-300 rounded-md px-2 py-1"
                      min="1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Label Template
                  </label>
                  <input
                    type="text"
                    value={step.loop.labelTemplate || 'Item {index}'}
                    disabled={mode !== 'edit'}
                    onChange={(e) => {
                      executeIfEditable(() =>
                        onStepChange({
                          ...step,
                          loop: {
                            ...step.loop!,
                            labelTemplate: e.target.value,
                          },
                        })
                      );
                    }}
                    className="w-full text-sm border border-gray-300 rounded-md px-2 py-1"
                    placeholder="e.g., Guest {index}, Item {index}"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use {'{index}'} for item number (starts from 1)
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add Field Selector */}
        {showAddField && (
          <div className="mt-3 p-3 bg-white border rounded-lg">
            <h4 className="text-sm font-medium mb-2">Person fields (reserved keys)</h4>
            <p className="text-xs text-gray-500 mb-2">
              Use these for event registration so Salekit / DC get first_name,
              last_name, email, mobile_number.
            </p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {PERSON_FIELD_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => handleAddField(preset.inputType, preset.key)}
                  className="p-2 border rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left transition-colors"
                >
                  <div className="text-xs font-medium">{preset.label}</div>
                  <div className="text-xs text-gray-500 font-mono">
                    {preset.key}
                  </div>
                </button>
              ))}
            </div>
            <h4 className="text-sm font-medium mb-2">Choose Field Type:</h4>
            {isEventFlow && (
              <p className="text-xs text-amber-700 mb-2">
                Email / Phone are hidden here — use Person fields above (
                <span className="font-mono">email</span>,{' '}
                <span className="font-mono">mobile_number</span>).
              </p>
            )}
            <div className="grid grid-cols-2 gap-2">
              {choosableFieldTypes.map((fieldType) => {
                const Icon = fieldType.icon;
                return (
                  <button
                    key={fieldType.id}
                    onClick={() => handleAddField(fieldType.id)}
                    className="p-2 border rounded-lg hover:border-green-500 hover:bg-green-50 text-left transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-gray-600" />
                      <div>
                        <div className="text-xs font-medium">
                          {fieldType.label}
                        </div>
                        <div className="text-xs text-gray-500">
                          {fieldType.description}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setShowAddField(false)}
                className="px-3 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Fields List & Editor */}
      <div className="flex-1 flex">
        {/* Fields List */}
        <div className="w-1/2 border-r overflow-y-auto">
          {step.fields.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="text-4xl mb-2">📝</div>
              <p className="text-sm">No fields yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Click "Add Field" to get started
              </p>
            </div>
          ) : (
            <div className="p-2 space-y-2">
              {step.fields.map((field, index) => {
                const fieldType = FIELD_TYPES.find((t) => t.id === field.type);
                const Icon = fieldType?.icon || Type;

                return (
                  <div
                    key={field.id}
                    className={`border rounded-lg p-3 cursor-pointer transition-all ${
                      selectedFieldId === field.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    onClick={() => onSelectField(field.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-start gap-2">
                          <Icon className="w-4 h-4 text-gray-600 shrink-0" />
                          <span className="text-sm font-medium leading-none">
                            <MDEditor.Markdown
                              source={field.label}
                              className="bg-inherit line-clamp-2 [&>*]:leading-tight [&>*]:m-0 select-none"
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            />
                          </span>
                          {field.required && (
                            <span className="text-red-400 text-xs">*</span>
                          )}
                          {field.binding && (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                field.binding.name &&
                                field.binding.name.trim() !== ''
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              🔗 {field.binding.name || 'No Name'}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 select-none">
                          {fieldType?.label}
                        </div>
                      </div>

                      {/* Field Actions */}
                      {mode === 'edit' && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveField(field.id, 'up');
                            }}
                            disabled={index === 0}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveField(field.id, 'down');
                            }}
                            disabled={index === step.fields.length - 1}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(`Delete field "${field.label}"?`)) {
                                handleDeleteField(field.id);
                              }
                            }}
                            className="p-1 text-red-400 hover:text-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Field Editor */}
        <div className="w-1/2 overflow-y-auto">
          {selectedField ? (
            <FieldEditor
              field={selectedField}
              onUpdate={(updates) =>
                handleUpdateField(selectedField.id, updates)
              }
              allSteps={allSteps}
            />
          ) : (
            <div className="p-8 text-center text-gray-500">
              <div className="text-4xl mb-2">👈</div>
              <p className="text-sm">Select a field to edit</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Field Editor Component
interface FieldEditorProps {
  field: Field;
  onUpdate: (updates: Partial<Field>) => void;
  allSteps?: Step[];
}

function FieldEditor({ field, onUpdate, allSteps }: FieldEditorProps) {
  const fieldType = FIELD_TYPES.find((t) => t.id === field.type);
  const Icon = fieldType?.icon || Type;
  const [isEditingFieldKey, setIsEditingFieldKey] = useState(false);
  const { mode, canEditLabels, canEditStructure } = useFlowBuilder();

  const handleAddOption = () => {
    const newOption = {
      id: `option_${Date.now()}`,
      label: `Option ${(field.options?.length || 0) + 1}`,
      value: `option${(field.options?.length || 0) + 1}`,
    };

    onUpdate({
      options: [...(field.options || []), newOption],
    });
  };

  const handleUpdateOption = (
    optionId: string,
    updates: { label?: string; value?: string }
  ) => {
    onUpdate({
      options: field.options?.map((option) =>
        option.id === optionId ? { ...option, ...updates } : option
      ),
    });
  };

  const handleDeleteOption = (optionId: string) => {
    onUpdate({
      options: field.options?.filter((option) => option.id !== optionId),
    });
  };

  const executeIfEditable = (fn: Function) => {
    if (mode !== 'edit') {
      return;
    }
    fn();
  };

  const executeIfCanEditLabels = (fn: Function) => {
    if (!canEditLabels) {
      return;
    }
    fn();
  };

  return (
    <div className="p-4 space-y-4">
      {/* Field Header */}
      <div className="flex items-center gap-2 pb-2 border-b">
        <Icon className="w-5 h-5 text-gray-600" />
        <div>
          <h4 className="font-medium">Edit Field</h4>
          <p className="text-xs text-gray-500">{fieldType?.label}</p>
        </div>
      </div>

      {/* Basic Settings */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Field Label *
          </label>
          <MarkdownEditor
            value={field.label}
            onChange={(value) => {
              const label = value ?? '';
              // Do not overwrite reserved person fieldKeys when the label changes
              if (mode === 'edit' && !isReservedFieldKey(field.fieldKey)) {
                const nextKey = normalizeToVar(label);
                const warn = warnIfSlugCollidesWithReserved(
                  nextKey,
                  field.fieldKey
                );
                if (warn) {
                  // Keep label update but do not auto-assign reserved slug
                  onUpdate({ label });
                  return;
                }
                onUpdate({ label, fieldKey: nextKey });
                return;
              }
              onUpdate({ label });
            }}
          />
          {isReservedFieldKey(field.fieldKey) && (
            <p className="text-xs text-blue-600 mt-1">
              Reserved fieldKey <span className="font-mono">{field.fieldKey}</span>{' '}
              — label changes will not rename this key (needed for registration /
              Salekit).
            </p>
          )}
        </div>

        {FIELD_TYPES.find((type) => type.id === field.type)?.hasPlaceholder && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Placeholder Text
            </label>
            <input
              type="text"
              value={field.placeholder || ''}
              onChange={(e) => {
                onUpdate({ placeholder: e.target.value });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="Enter placeholder text"
            />
          </div>
        )}

        <div className="">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Field Key *
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              disabled={
                !isEditingFieldKey ||
                mode !== 'edit' ||
                isReservedFieldKey(field.fieldKey)
              }
              value={field.fieldKey || ''}
              onChange={(e) => {
                executeIfEditable(() => onUpdate({ fieldKey: e.target.value }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              required
            />
            {mode === 'edit' && !isReservedFieldKey(field.fieldKey) && (
              <button
                type="button"
                onClick={() => setIsEditingFieldKey((prev) => !prev)}
                className="px-2 py-2 text-sm border rounded-md hover:bg-gray-50 h-full"
              >
                {isEditingFieldKey ? 'Lock' : 'Edit'}
              </button>
            )}
            {mode === 'edit' && isReservedFieldKey(field.fieldKey) && (
              <span className="text-xs text-gray-500 whitespace-nowrap">
                Locked
              </span>
            )}
          </div>
          {!isReservedFieldKey(field.fieldKey) && (
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Assign reserved person key
              </label>
              <select
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                value=""
                disabled={mode !== 'edit'}
                onChange={(e) => {
                  const key = e.target.value;
                  if (!key) return;
                  executeIfEditable(() =>
                    onUpdate({
                      fieldKey: key,
                      ...(key === 'event_id'
                        ? {
                            binding: {
                              type: 'query_param' as const,
                              name: 'eventId',
                              required: false,
                            },
                          }
                        : {}),
                    })
                  );
                }}
              >
                <option value="">— optional —</option>
                {PERSON_FIELD_PRESETS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label} ({p.key})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            checked={field.required}
            disabled={mode !== 'edit'}
            onChange={(e) => {
              executeIfEditable(() => onUpdate({ required: e.target.checked }));
            }}
            className="rounded border-gray-300"
          />
          <label className="ml-2 text-sm text-gray-700">Required field</label>
        </div>
      </div>

      {/* Binding Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h5 className="text-sm font-medium text-gray-700">Binding</h5>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={!!field.binding}
              disabled={mode !== 'edit'}
              onChange={(e) => {
                executeIfEditable(() => {
                  if (e.target.checked) {
                    // Prompt user to enter binding name first
                    const bindingName = prompt(
                      'Please enter binding name (e.g., userId, eventId):'
                    );
                    if (bindingName && bindingName.trim() !== '') {
                      onUpdate({
                        binding: {
                          type: 'query_param',
                          name: bindingName.trim(),
                          required: false,
                          fallback: '',
                        },
                      });
                    } else {
                      // User cancelled or entered empty name
                      return;
                    }
                  } else {
                    onUpdate({ binding: undefined });
                  }
                });
              }}
              className="rounded border-gray-300"
            />
            <label className="ml-2 text-xs text-gray-600">Enable binding</label>
          </div>
        </div>

        {field.binding && (
          <div className="space-y-3 p-3 bg-gray-50 rounded-md">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Binding Type
                </label>
                <select
                  value={field.binding.type}
                  onChange={(e) =>
                    executeIfEditable(() =>
                      onUpdate({
                        binding: {
                          ...field.binding!,
                          type: e.target.value as 'query_param',
                        },
                      })
                    )
                  }
                  className="w-full text-sm border border-gray-300 rounded-md px-2 py-1"
                >
                  <option value="query_param">Query Parameter</option>
                  {/* Add other binding types here if needed */}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Binding Name *
                </label>
                <input
                  type="text"
                  value={field.binding.name}
                  disabled={mode !== 'edit'}
                  onChange={(e) => {
                    executeIfEditable(() =>
                      onUpdate({
                        binding: {
                          ...field.binding!,
                          name: e.target.value,
                        },
                      })
                    );
                  }}
                  className="w-full text-sm border border-gray-300 rounded-md px-2 py-1"
                  placeholder="e.g., userId, itemId"
                  required
                />
                {!field.binding.name && (
                  <p className="text-xs text-red-500 mt-1">
                    Binding name is required
                  </p>
                )}
              </div>
            </div>

            {field.binding.type === 'query_param' && (
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={field.binding.required || false}
                    disabled={mode !== 'edit'}
                    onChange={(e) => {
                      executeIfEditable(() =>
                        onUpdate({
                          binding: {
                            ...field.binding!,
                            required: e.target.checked,
                          },
                        })
                      );
                    }}
                    className="rounded border-gray-300"
                  />
                  <label className="ml-2 text-xs text-gray-600">Required</label>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Fallback Value
                  </label>
                  <input
                    type="text"
                    value={field.binding.fallback || ''}
                    disabled={mode !== 'edit'}
                    onChange={(e) => {
                      executeIfEditable(() =>
                        onUpdate({
                          binding: {
                            ...field.binding!,
                            fallback: e.target.value,
                          },
                        })
                      );
                    }}
                    className="w-full text-sm border border-gray-300 rounded-md px-2 py-1"
                    placeholder="e.g., 0, default_value"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Options for Choice Fields */}
      {['single_choice', 'multiple_choice'].includes(field.type) && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-medium text-gray-700">Options</h5>
            {mode === 'edit' && (
              <button
                onClick={handleAddOption}
                className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
              >
                Add Option
              </button>
            )}
          </div>

          <div className="space-y-2">
            {field.options?.map((option, index) => (
              <div key={option.id} className="flex flex-col gap-1">
                {/* แถวแรก: เลขลำดับ + label + checkbox + ปุ่มลบ */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-6">
                    {index + 1}.
                  </span>
                  <input
                    type="text"
                    value={option.label}
                    disabled={mode !== 'edit'}
                    onChange={(e) => {
                      handleUpdateOption(option.id, {
                        label: e.target.value,
                        value: e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, '_'),
                      });
                    }}
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="Option label"
                  />
                  <label className="inline-flex items-center gap-1 cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={option.allowOther ?? false}
                      disabled={mode !== 'edit'}
                      onChange={(e) => {
                        handleUpdateOption(option.id, {
                          allowOther: e.target.checked,
                          otherPlaceholder: e.target.checked
                            ? (option.otherPlaceholder ?? '')
                            : undefined,
                        } as any);
                      }}
                      className="rounded border-gray-300"
                    />
                    <span className="text-xs text-gray-600">
                      Allow "Other"
                    </span>
                  </label>
                  {mode === 'edit' && (
                    <button
                      onClick={() => handleDeleteOption(option.id)}
                      className="p-1 text-red-400 hover:text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {option.allowOther && (
                  <div className="pl-6">
                    <input
                      type="text"
                      value={option.otherPlaceholder ?? ''}
                      disabled={mode !== 'edit'}
                      onChange={(e) => {
                        handleUpdateOption(option.id, {
                          otherPlaceholder: e.target.value,
                        } as any);
                      }}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm ml-2 mb-2"
                      placeholder="ระบุ placeholder"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {['text_input', 'textarea'].includes(field.type) && (
        <div>
          <label className="block text-xs text-gray-600 mb-1">Max Length</label>
          <input
            type="number"
            value={getValidationValue(field.validation, 'max_length') || ''}
            disabled={mode !== 'edit'}
            onChange={(e) => {
              const rawValue = e.target.value;
              const numericValue = rawValue ? Number(rawValue) : undefined;

              let max: number | undefined;
              if (numericValue == null || isNaN(numericValue)) {
                max = undefined;
              } else {
                max = numericValue <= 0 ? undefined : Math.max(0, numericValue); // prevents negative numbers
              }
              executeIfEditable(() =>
                onUpdate({
                  validation: {
                    ...field.validation,
                    max,
                    type: 'max_length',
                  },
                })
              );
            }}
            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
          />
        </div>
      )}

      {/* Validation for Number Fields */}
      {field.type === 'number_input' && (
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-gray-700">Validation</h5>

          {/* Debug validation object */}
          {process.env.NODE_ENV === 'development' && (
            <div className="p-2 bg-gray-100 rounded text-xs">
              <p>
                Debug: field.validation = {JSON.stringify(field.validation)}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Min Value
              </label>
              <input
                type="number"
                value={getValidationValue(field.validation, 'min') || ''}
                disabled={mode !== 'edit'}
                onChange={(e) =>
                  executeIfEditable(() =>
                    onUpdate({
                      validation: {
                        ...field.validation,
                        min: e.target.value
                          ? Number(e.target.value)
                          : undefined,
                        type: 'min',
                      },
                    })
                  )
                }
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Max Value
              </label>
              <input
                type="number"
                value={getValidationValue(field.validation, 'max') || ''}
                disabled={mode !== 'edit'}
                onChange={(e) =>
                  executeIfEditable(() =>
                    onUpdate({
                      validation: {
                        ...field.validation,
                        max: e.target.value
                          ? Number(e.target.value)
                          : undefined,
                        type: 'max',
                      },
                    })
                  )
                }
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {field.type === 'file_upload' && (
        <div className="flex gap-2">
          {FILE_TYPE_OPTIONS.map(({ id, label, value }) => (
            <div key={id} className="flex gap-1">
              <input
                type="checkbox"
                id={id}
                name="fileTypes"
                checked={field?.fileTypes?.includes(value) ?? false}
                disabled={mode !== 'edit'}
                onChange={(e) => {
                  const currentFileTypes = field?.fileTypes ?? [];
                  const checked = e.target.checked;

                  const updatedFileTypes = checked
                    ? [...currentFileTypes, value]
                    : currentFileTypes.filter((type) => type !== value);

                  executeIfEditable(() =>
                    onUpdate({ fileTypes: updatedFileTypes })
                  );
                }}
              />
              <label htmlFor={id}>{label}</label>
            </div>
          ))}
        </div>
      )}

      {field.type === 'rating' && (
        <RatingOptionsEditor
          options={(field.options as RatingOption[] | undefined) ?? []}
          canEditLabels={canEditLabels}
          canEditStructure={canEditStructure}
          onChange={(options) =>
            executeIfCanEditLabels(() => onUpdate({ options }))
          }
        />
      )}

      {field.type === 'consent' && (
        <ConsentConfig
          disabled={mode !== 'edit'}
          field={field}
          onUpdate={onUpdate}
          availableFields={allSteps?.map((s) => s.fields)?.flat() ?? []}
        />
      )}
    </div>
  );
}

interface RatingOptionsEditorProps {
  options: RatingOption[];
  canEditLabels?: boolean;
  canEditStructure?: boolean;
  onChange: (options: RatingOption[]) => void;
}

function RatingOptionsEditor({
  options,
  canEditLabels = false,
  canEditStructure = false,
  onChange,
}: RatingOptionsEditorProps) {
  const handleAddScore = () => {
    if (!canEditStructure) {
      return;
    }
    const nextScore = options.length + 1;
    onChange(
      renumberRatingOptions([
        ...options,
        {
          id: `rating_${Date.now()}`,
          score: nextScore,
          label: '',
          value: `score_${nextScore}`,
        },
      ])
    );
  };

  const handleDeleteScore = (optionId: string) => {
    if (!canEditStructure || options.length <= 1) {
      return;
    }
    onChange(
      renumberRatingOptions(options.filter((option) => option.id !== optionId))
    );
  };

  const handleUpdateOption = (
    optionId: string,
    updates: Partial<Pick<RatingOption, 'label' | 'value'>>
  ) => {
    onChange(
      options.map((option) => {
        if (option.id !== optionId) {
          return option;
        }

        const nextLabel =
          updates.label !== undefined ? updates.label : option.label;

        // Only auto-slug value from label when editing structure in default language.
        // In localize mode, labels must change without altering report values.
        let nextValue = option.value;
        if (updates.value !== undefined) {
          nextValue = updates.value;
        } else if (
          canEditStructure &&
          updates.label !== undefined &&
          updates.label.trim()
        ) {
          nextValue = slugifyRatingValue(updates.label) || option.value;
        }

        return {
          ...option,
          label: nextLabel,
          value: nextValue,
        };
      })
    );
  };

  return (
    <div className="space-y-3">
      <h5 className="text-sm font-medium text-gray-700">Rating Options</h5>

      <div className="overflow-hidden rounded-md border border-gray-200">
        <div className="grid grid-cols-[56px_1fr_1fr_32px] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600">
          <span>Score</span>
          <span>Label (Form)</span>
          <span>Value (Report)</span>
          <span />
        </div>

        <div className="divide-y divide-gray-100">
          {options.map((option, index) => (
            <div
              key={option.id}
              className="grid grid-cols-[56px_1fr_1fr_32px] items-center gap-2 px-3 py-2"
            >
              <span
                className={`inline-flex h-7 w-7 items-center justify-center rounded text-xs font-semibold ${
                  SCORE_BADGE_COLORS[index % SCORE_BADGE_COLORS.length]
                }`}
              >
                {option.score}
              </span>
              <input
                type="text"
                value={option.label ?? ''}
                disabled={!canEditLabels}
                onChange={(e) =>
                  handleUpdateOption(option.id, { label: e.target.value })
                }
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                placeholder="Optional"
              />
              <input
                type="text"
                value={option.value}
                disabled={!canEditStructure}
                onChange={(e) =>
                  handleUpdateOption(option.id, { value: e.target.value })
                }
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                placeholder="report_value"
              />
              {canEditStructure && options.length > 1 ? (
                <button
                  type="button"
                  onClick={() => handleDeleteScore(option.id)}
                  className="p-1 text-red-400 hover:text-red-600"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              ) : (
                <span />
              )}
            </div>
          ))}
        </div>
      </div>

      {canEditStructure && (
        <button
          type="button"
          onClick={handleAddScore}
          className="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
        >
          + Add Score
        </button>
      )}

      <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
        <p>Label = ข้อความที่ผู้ตอบเห็นบนฟอร์ม (ไม่บังคับ)</p>
        <p>Value = ค่าที่บันทึกใน response / CSV export</p>
        {!canEditStructure && canEditLabels && (
          <p className="mt-1">
            ในโหมด localize สามารถแก้ไข Label ได้เท่านั้น — Score และ Value
            ใช้จากภาษาหลัก
          </p>
        )}
      </div>
    </div>
  );
}
